import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { documentsApi } from '../services/api'
import { formatDateTime, formatRecent, parseUTC } from '../utils/dateUtils'
import { describeFile, formatFileSize } from '../utils/fileTypes'
import ViewModeToggle from '../components/collection/ViewModeToggle'
import CollectionGrid from '../components/collection/CollectionGrid'
import MetaChip from '../components/collection/MetaChip'
import { trackSpotlight, useCardLink } from '../components/collection/cardInteractions'
import { FileGlyph, FileTile, DocumentStatusChip } from '../components/documents/FileGlyph'
import useStoredViewMode from '../hooks/useStoredViewMode'
import {
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  RefreshCw,
  AlertCircle,
  Files,
  FileSearch,
  X,
  Check,
  ArrowUpDown,
  ArrowRight,
  Mail,
  FileStack,
  Upload,
} from 'lucide-react'

const PAGE_SIZES = [12, 24, 48]
const SEARCH_DEBOUNCE_MS = 300

// Sorting reorders the page the server returned (newest first); CollectionGrid
// glides rows and cards into their new places.
const byDate = d => (parseUTC(d.created_at) || new Date(0)).getTime()
const byName = d => (d.filename || '').toLowerCase()
const SORTS = [
  { value: 'recent', label: 'Newest first', column: 'date', compare: (a, b) => byDate(b) - byDate(a) },
  { value: 'oldest', label: 'Oldest first', column: 'date', compare: (a, b) => byDate(a) - byDate(b) },
  { value: 'name', label: 'Name A–Z', column: 'name', compare: (a, b) => byName(a).localeCompare(byName(b)) },
  { value: 'name_desc', label: 'Name Z–A', column: 'name', compare: (a, b) => byName(b).localeCompare(byName(a)) },
  { value: 'size', label: 'Largest first', column: 'size', compare: (a, b) => (b.file_size_bytes || 0) - (a.file_size_bytes || 0) },
  { value: 'size_asc', label: 'Smallest first', column: 'size', compare: (a, b) => (a.file_size_bytes || 0) - (b.file_size_bytes || 0) },
]
// Clicking a column header flips between its pair of sorts.
const COLUMN_SORTS = {
  name: ['name', 'name_desc'],
  size: ['size', 'size_asc'],
  date: ['recent', 'oldest'],
}

// File · Status · Size · Source · Uploaded · open affordance
const LIST_COLUMNS = 'grid-cols-[minmax(0,1fr)_auto] md:grid-cols-[minmax(0,1fr)_7.5rem_5.5rem_9rem_8.5rem_2.5rem]'
const GRID_COLUMNS = 'grid-cols-[repeat(auto-fill,minmax(min(100%,15.5rem),1fr))]'

function Documents() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [documents, setDocuments] = useState([])
  const [total, setTotal] = useState(0)
  const [perPage, setPerPage] = useState(PAGE_SIZES[0])
  const [loading, setLoading] = useState(true)
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState(null)
  const [viewMode, setViewMode] = useStoredViewMode('documents')
  const requestSeq = useRef(0)

  const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1)
  const search = searchParams.get('search') || ''
  const sortValue = SORTS.some(s => s.value === searchParams.get('sort')) ? searchParams.get('sort') : 'recent'
  const sort = SORTS.find(s => s.value === sortValue)

  const updateParams = useCallback((changes, options) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      Object.entries(changes).forEach(([key, value]) => {
        if (value === '' || value == null || (key === 'page' && value === 1) || (key === 'sort' && value === 'recent')) next.delete(key)
        else next.set(key, String(value))
      })
      return next
    }, options)
  }, [setSearchParams])

  // Live search: the box updates instantly, the URL (and the fetch) follows
  // once typing pauses. Back/forward still restores the box from the URL.
  const [query, setQuery] = useState(search)
  // Keep what's typed (a trailing space mid-word) when the URL only caught up.
  useEffect(() => { setQuery(q => (q.trim() === search ? q : search)) }, [search])
  useEffect(() => {
    if (query.trim() === search) return undefined
    const timer = setTimeout(() => updateParams({ search: query.trim(), page: 1 }, { replace: true }), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [query, search, updateParams])
  const clearSearch = () => {
    setQuery('')
    updateParams({ search: '', page: 1 }, { replace: true })
  }

  const fetchDocuments = useCallback(async () => {
    // Only the latest request may write state, so a slow response for an
    // earlier search can't overwrite a newer one.
    const seq = ++requestSeq.current
    try {
      setLoading(true)
      const res = await documentsApi.list({ page, per_page: perPage, search: search || undefined })
      if (seq !== requestSeq.current) return
      setDocuments(res.data.documents || [])
      setTotal(res.data.pagination?.total || 0)
      setError(null)
    } catch (err) {
      if (seq !== requestSeq.current) return
      console.error('Error fetching documents:', err)
      setError('We couldn’t load your documents. Check your connection and try again.')
    } finally {
      if (seq === requestSeq.current) {
        setLoading(false)
        setLoaded(true)
      }
    }
  }, [page, perPage, search])

  useEffect(() => { fetchDocuments() }, [fetchDocuments])

  const sortedDocuments = useMemo(() => [...documents].sort(sort.compare), [documents, sort])
  const totalPages = Math.max(1, Math.ceil(total / perPage))

  // A page past the end (after a search narrowed things) snaps back.
  useEffect(() => {
    if (loaded && !loading && page > totalPages) updateParams({ page: totalPages }, { replace: true })
  }, [loaded, loading, page, totalPages, updateParams])

  const handleColumnSort = (column) => {
    const [first, second] = COLUMN_SORTS[column]
    updateParams({ sort: sortValue === first ? second : first }, { replace: true })
  }

  const goToPage = (next) => {
    updateParams({ page: next })
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  if (!loaded) {
    return (
      <div className="space-y-6">
        <PageHeader total={null} />
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
          <ToolbarSkeleton />
          {viewMode === 'grid' ? (
            <div className={`grid gap-4 p-4 ${GRID_COLUMNS}`}>
              {[...Array(8)].map((_, i) => <DocumentCardSkeleton key={i} />)}
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {[...Array(6)].map((_, i) => <DocumentRowSkeleton key={i} />)}
            </div>
          )}
        </div>
      </div>
    )
  }

  if (error && documents.length === 0) {
    return (
      <div className="space-y-6">
        <PageHeader total={null} />
        <div className="mx-auto mt-6 max-w-md animate-panel-in">
          <div className="rounded-2xl border border-red-100 bg-red-50 p-6 dark:border-red-900/50 dark:bg-red-900/20">
            <div className="flex items-start gap-4">
              <div className="rounded-full bg-red-100 p-3 dark:bg-red-900/40">
                <AlertCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-red-800 dark:text-red-300">Documents didn&rsquo;t load</h3>
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>
                <button
                  onClick={fetchDocuments}
                  disabled={loading}
                  className="mt-4 inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                  Try again
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const rangeStart = total === 0 ? 0 : (page - 1) * perPage + 1
  const rangeEnd = Math.min(page * perPage, total)

  return (
    <div className="space-y-6 animate-view-in">
      <PageHeader total={total} />

      <div className="rounded-2xl border border-gray-200 bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)] dark:border-gray-800 dark:bg-gray-900">
        {/* Toolbar */}
        <div className="flex flex-col gap-3 border-b border-gray-100 px-4 py-3.5 dark:border-gray-800 sm:px-5 lg:flex-row lg:items-center">
          <SearchBox value={query} onChange={setQuery} onClear={clearSearch} busy={loading && Boolean(search)} />
          <div className="flex items-center gap-2 lg:ml-auto">
            <SortMenu value={sortValue} onChange={(value) => updateParams({ sort: value }, { replace: true })} />
            <ViewModeToggle value={viewMode} onChange={setViewMode} />
            <button
              type="button"
              onClick={fetchDocuments}
              disabled={loading}
              aria-label="Refresh documents"
              title="Refresh"
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-500 transition-all duration-200 hover:bg-gray-50 hover:text-gray-700 enabled:active:scale-95 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Summary line */}
        <div className="flex min-h-[2.75rem] flex-wrap items-center gap-x-2 gap-y-1 px-4 pt-3 text-sm text-gray-500 dark:text-gray-400 sm:px-5">
          {total > 0 ? (
            <span>
              Showing <span className="font-medium text-gray-900 tabular-nums dark:text-gray-100">{rangeStart}&ndash;{rangeEnd}</span> of{' '}
              <span className="font-medium text-gray-900 tabular-nums dark:text-gray-100">{total}</span> document{total !== 1 ? 's' : ''}
            </span>
          ) : (
            <span>No documents</span>
          )}
          {search && (
            <span className="inline-flex items-center gap-1 rounded-full bg-navy-50 py-0.5 pl-2.5 pr-1 text-xs font-medium text-navy-700 ring-1 ring-inset ring-navy-200/70 animate-panel-in dark:bg-navy-500/10 dark:text-navy-200 dark:ring-navy-400/20">
              <span className="max-w-[16rem] truncate">Matching &ldquo;{search}&rdquo;</span>
              <button
                type="button"
                onClick={clearSearch}
                aria-label="Clear search"
                className="rounded-full p-0.5 transition-colors hover:bg-navy-100 dark:hover:bg-navy-400/20"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
        </div>

        {error && (
          <div className="mx-4 mt-3 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 animate-panel-in dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300 sm:mx-5">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span className="flex-1">{error}</span>
            <button type="button" onClick={fetchDocuments} className="font-semibold hover:underline">Retry</button>
          </div>
        )}

        {/* Refetch over existing results: a sliver of progress, and the
            collection dims instead of blanking. */}
        <div className="relative mt-3 h-0.5 overflow-hidden" aria-hidden="true">
          {loading && (
            <span className="collection-progress-bar absolute inset-y-0 left-0 w-1/3 rounded-full bg-navy-500/80 dark:bg-navy-400/80" />
          )}
        </div>

        <div key={viewMode} className="animate-view-in">
          {documents.length === 0 ? (
            <EmptyState search={search} onClear={clearSearch} />
          ) : viewMode === 'list' ? (
            <div role="table" aria-label="Documents">
              <div role="row" className={`hidden items-center gap-4 border-y border-gray-100 bg-gray-50/70 px-5 py-2.5 text-xs font-semibold text-gray-500 dark:border-gray-800 dark:bg-gray-800/40 dark:text-gray-400 md:grid ${LIST_COLUMNS}`}>
                <ColumnHeader label="File" column="name" sort={sort} onSort={handleColumnSort} />
                <span role="columnheader">Status</span>
                <ColumnHeader label="Size" column="size" sort={sort} onSort={handleColumnSort} />
                <span role="columnheader">Source</span>
                <ColumnHeader label="Uploaded" column="date" sort={sort} onSort={handleColumnSort} />
                <span role="columnheader" className="sr-only">Open</span>
              </div>
              {/* Keyed on which documents are shown, not their order: a new page
                  or search replays the entrance, re-sorting glides into place. */}
              <CollectionGrid
                key={documents.map(d => d.id).sort().join('-')}
                items={sortedDocuments}
                getKey={d => d.id}
                busy={loading}
                className="divide-y divide-gray-100 dark:divide-gray-800"
              >
                {(doc) => <DocumentRow document={doc} />}
              </CollectionGrid>
            </div>
          ) : (
            <CollectionGrid
              key={documents.map(d => d.id).sort().join('-')}
              items={sortedDocuments}
              getKey={d => d.id}
              busy={loading}
              className={`grid gap-4 p-4 sm:p-5 ${GRID_COLUMNS}`}
            >
              {(doc) => <DocumentCard document={doc} />}
            </CollectionGrid>
          )}
        </div>

        <Pagination
          page={page}
          totalPages={totalPages}
          total={total}
          perPage={perPage}
          onPage={goToPage}
          onPerPage={(size) => { setPerPage(size); updateParams({ page: 1 }, { replace: true }) }}
        />
      </div>
    </div>
  )
}

// ── Header & toolbar ─────────────────────────────────────────────────────────

function PageHeader({ total }) {
  return (
    <div className="flex items-center gap-3.5 animate-header-in">
      <div className="hidden h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-navy-700 to-navy-900 text-white shadow-sm ring-1 ring-navy-900/10 sm:flex">
        <Files className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-2.5">
          <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100">Documents</h1>
          {total != null && (
            <span key={total} className="inline-flex items-center rounded-full bg-navy-50 px-2.5 py-0.5 text-xs font-semibold tabular-nums text-navy-700 ring-1 ring-inset ring-navy-200 animate-panel-in dark:bg-navy-800/50 dark:text-navy-200 dark:ring-navy-700">
              {total}
            </span>
          )}
        </div>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Every file received or uploaded, ready to preview and trace back to its requests.</p>
      </div>
    </div>
  )
}

function SearchBox({ value, onChange, onClear, busy }) {
  const inputRef = useRef(null)
  const [focused, setFocused] = useState(false)

  // "/" jumps to search from anywhere on the page, as in most list views.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return
      const target = e.target
      if (target.closest?.('input, textarea, select, [contenteditable="true"]')) return
      e.preventDefault()
      inputRef.current?.focus()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  return (
    <div className="relative w-full lg:max-w-md">
      <Search className={`pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transition-colors duration-200 ${focused ? 'text-navy-500 dark:text-navy-300' : 'text-gray-400'}`} />
      <input
        ref={inputRef}
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            if (value) onClear()
            else e.currentTarget.blur()
          }
        }}
        placeholder="Search by file name…"
        aria-label="Search documents by file name"
        className="h-9 w-full rounded-lg border border-gray-200 bg-white pl-9 pr-16 text-sm text-gray-900 transition-[border-color,box-shadow] duration-200 placeholder:text-gray-400 focus:border-navy-500 focus:outline-none focus:ring-2 focus:ring-navy-500/25 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 [&::-webkit-search-cancel-button]:hidden"
      />
      <div className="absolute inset-y-0 right-2 flex items-center gap-1">
        {busy && <RefreshCw className="h-3.5 w-3.5 animate-spin text-gray-400" aria-hidden="true" />}
        {value ? (
          <button
            type="button"
            onClick={() => { onClear(); inputRef.current?.focus() }}
            aria-label="Clear search"
            className="rounded-md p-1 text-gray-400 transition-colors animate-panel-in hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-200"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : (
          <kbd className={`hidden rounded border border-gray-200 bg-gray-50 px-1.5 font-sans text-[11px] font-medium leading-5 text-gray-400 transition-opacity duration-200 dark:border-gray-700 dark:bg-gray-900 sm:inline-block ${focused ? 'opacity-0' : 'opacity-100'}`}>
            /
          </kbd>
        )}
      </div>
    </div>
  )
}

function SortMenu({ value, onChange }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  const current = SORTS.find(s => s.value === value) || SORTS[0]

  useEffect(() => {
    if (!open) return undefined
    const onPointer = (e) => { if (!ref.current?.contains(e.target)) setOpen(false) }
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`inline-flex h-9 items-center gap-2 rounded-lg border px-3 text-[13px] font-medium transition-colors duration-200 ${
          open
            ? 'border-navy-300 bg-navy-50/60 text-gray-900 dark:border-navy-500/40 dark:bg-navy-500/10 dark:text-gray-100'
            : 'border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-gray-100'
        }`}
      >
        <ArrowUpDown className="h-3.5 w-3.5 text-gray-400" />
        <span className="hidden sm:inline">{current.label}</span>
        <span className="sr-only sm:hidden">Sort: {current.label}</span>
        <ChevronDown className={`h-3.5 w-3.5 text-gray-400 transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <ul
          role="listbox"
          aria-label="Sort documents"
          className="absolute left-0 z-30 mt-2 w-48 origin-top-left sm:left-auto sm:right-0 sm:origin-top-right rounded-xl border border-gray-200 bg-white p-1.5 shadow-xl ring-1 ring-black/5 animate-fade-in dark:border-gray-700 dark:bg-gray-800"
        >
          {SORTS.map(option => {
            const selected = option.value === value
            return (
              <li key={option.value}>
                <button
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => { onChange(option.value); setOpen(false) }}
                  className={`flex w-full items-center justify-between gap-2 rounded-lg px-2.5 py-2 text-left text-sm transition-colors ${
                    selected
                      ? 'bg-navy-50 font-medium text-navy-700 dark:bg-navy-500/15 dark:text-navy-200'
                      : 'text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700/60'
                  }`}
                >
                  {option.label}
                  {selected && <Check className="h-3.5 w-3.5" />}
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

function ColumnHeader({ label, column, sort, onSort }) {
  const active = sort.column === column
  const ascending = ['name', 'size_asc', 'oldest'].includes(sort.value)
  return (
    <span role="columnheader" aria-sort={active ? (ascending ? 'ascending' : 'descending') : 'none'}>
      <button
        type="button"
        onClick={() => onSort(column)}
        className={`group/sort inline-flex items-center gap-1 transition-colors hover:text-gray-800 dark:hover:text-gray-200 ${active ? 'text-gray-800 dark:text-gray-200' : ''}`}
      >
        {label}
        <svg className={`h-3 w-3 transition-colors ${active ? 'text-navy-600 dark:text-navy-300' : 'text-gray-300 group-hover/sort:text-gray-400 dark:text-gray-600'}`} viewBox="0 0 10 14" fill="currentColor" aria-hidden="true">
          <path d="M5 0L9.33 5H0.67L5 0Z" className={`transition-opacity ${active && ascending ? 'opacity-100' : 'opacity-30'}`} />
          <path d="M5 14L0.67 9H9.33L5 14Z" className={`transition-opacity ${active && !ascending ? 'opacity-100' : 'opacity-30'}`} />
        </svg>
      </button>
    </span>
  )
}

// ── Rows & cards ─────────────────────────────────────────────────────────────

// Where the file came from: the email it arrived on, the request it was
// uploaded to, or neither.
function SourceChip({ document: doc }) {
  if (doc.email_id) {
    return (
      <Link to={`/emails/${doc.email_id}`} title="Open the email this arrived on" className="inline-flex min-w-0 rounded-md transition-transform duration-200 hover:-translate-y-px">
        <MetaChip icon={Mail} accent>Email</MetaChip>
      </Link>
    )
  }
  if (doc.request_id) {
    return (
      <Link to={`/requests/${doc.request_id}`} title="Open the request this was uploaded to" className="inline-flex min-w-0 rounded-md transition-transform duration-200 hover:-translate-y-px">
        <MetaChip icon={FileStack} accent>Request #{String(doc.request_id).substring(0, 8)}</MetaChip>
      </Link>
    )
  }
  return <MetaChip icon={Upload}>Upload</MetaChip>
}

function DocumentRow({ document: doc }) {
  const href = `/documents/${doc.id}`
  const cardLink = useCardLink(href)
  const file = describeFile(doc.filename, doc.content_type)

  return (
    <div
      role="row"
      {...cardLink}
      className={`group relative grid cursor-pointer items-center gap-4 px-4 py-3 transition-colors duration-200 hover:bg-navy-50/40 has-[a:focus-visible]:bg-navy-50/40 dark:hover:bg-navy-500/[0.06] sm:px-5 ${LIST_COLUMNS}`}
    >
      {/* Accent bar that grows in from the middle on hover */}
      <span aria-hidden="true" className={`pointer-events-none absolute inset-y-2 left-0 w-[3px] origin-center scale-y-0 rounded-r-full transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-y-100 ${file.swatch}`} />

      <div role="cell" className="flex min-w-0 items-center gap-3.5">
        <FileTile file={file} />
        <div className="min-w-0">
          <Link
            to={href}
            title={doc.filename}
            className="block truncate text-sm font-semibold text-gray-900 transition-colors focus:outline-none group-hover:text-navy-700 dark:text-gray-100 dark:group-hover:text-white"
          >
            {doc.filename || 'Untitled document'}
          </Link>
          <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">
            {file.label}
            {/* Size, status and date move under the name on small screens */}
            <span className="md:hidden"> · {formatFileSize(doc.file_size_bytes)} · {formatRecent(doc.created_at)}</span>
          </p>
        </div>
      </div>
      <div role="cell" className="hidden md:block">
        <DocumentStatusChip status={doc.status} displayName={doc.status_display} />
      </div>
      <div role="cell" className="hidden text-sm tabular-nums text-gray-600 dark:text-gray-400 md:block">
        {formatFileSize(doc.file_size_bytes)}
      </div>
      <div role="cell" className="hidden min-w-0 md:block">
        <SourceChip document={doc} />
      </div>
      <div role="cell" className="hidden md:block">
        <time dateTime={doc.created_at || undefined} title={formatDateTime(doc.created_at)} className="text-sm text-gray-600 dark:text-gray-400">
          {formatRecent(doc.created_at)}
        </time>
      </div>
      <div role="cell" className="flex justify-end">
        <span aria-hidden="true" className="flex h-8 w-8 items-center justify-center rounded-full text-navy-600 opacity-0 transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-safe:-translate-x-1.5 group-hover:translate-x-0 group-hover:bg-navy-100/60 group-hover:opacity-100 dark:text-navy-300 dark:group-hover:bg-navy-500/15">
          <ArrowRight className="h-4 w-4" />
        </span>
      </div>
    </div>
  )
}

function DocumentCard({ document: doc }) {
  const href = `/documents/${doc.id}`
  const cardLink = useCardLink(href)
  const file = describeFile(doc.filename, doc.content_type)

  return (
    <article
      {...cardLink}
      onPointerMove={trackSpotlight}
      style={{ '--card-glow': file.rgb }}
      className="collection-card group relative flex h-full cursor-pointer flex-col rounded-2xl border border-gray-200 bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)] transition-[transform,box-shadow,border-color] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] hover:border-gray-300 hover:shadow-[0_18px_40px_-22px_rgba(16,24,40,0.35)] motion-safe:hover:-translate-y-1 motion-safe:active:translate-y-0 motion-safe:active:scale-[0.985] has-[a:focus-visible]:ring-2 has-[a:focus-visible]:ring-navy-500/50 has-[a:focus-visible]:ring-offset-2 dark:border-gray-800 dark:bg-gray-900 dark:hover:border-gray-700 dark:hover:shadow-[0_18px_40px_-22px_rgba(0,0,0,0.8)] dark:has-[a:focus-visible]:ring-offset-gray-950"
    >
      {/* Preview band: the file's page, tinted by kind */}
      <div className={`relative flex h-32 items-center justify-center overflow-hidden rounded-t-[15px] border-b border-gray-100 bg-gradient-to-b to-transparent dark:border-gray-800 ${file.wash}`}>
        <div aria-hidden="true" className="absolute inset-0 opacity-[0.35] [background-image:radial-gradient(rgb(var(--gray-300))_0.75px,transparent_0.75px)] [background-size:12px_12px] [mask-image:radial-gradient(ellipse_at_center,black_20%,transparent_75%)] dark:opacity-20" />
        <FileGlyph file={file} className="h-[5.25rem] w-16" />
        <DocumentStatusChip status={doc.status} displayName={doc.status_display} className="absolute left-3 top-3 bg-white/80 backdrop-blur-sm dark:bg-gray-900/70" />
      </div>

      <div className="flex flex-1 flex-col p-4">
        <h3 className="line-clamp-2 text-[14.5px] font-semibold leading-5 tracking-tight text-gray-900 [overflow-wrap:anywhere] dark:text-gray-50">
          <Link to={href} title={doc.filename} className="rounded-sm transition-colors focus:outline-none group-hover:text-navy-700 dark:group-hover:text-white">
            {doc.filename || 'Untitled document'}
          </Link>
        </h3>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {file.label} <span aria-hidden="true" className="text-gray-300 dark:text-gray-600">·</span> <span className="tabular-nums">{formatFileSize(doc.file_size_bytes)}</span>
        </p>
        <div className="mb-4 mt-3 flex flex-wrap items-center gap-1.5">
          <SourceChip document={doc} />
        </div>

        <footer className="mt-auto flex items-center justify-between gap-3 border-t border-gray-100 pt-3 dark:border-gray-800">
          <time dateTime={doc.created_at || undefined} title={formatDateTime(doc.created_at)} className="whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
            {formatRecent(doc.created_at)}
          </time>
          <span aria-hidden="true" className="inline-flex flex-shrink-0 items-center gap-1 text-xs font-semibold text-navy-600 opacity-0 transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-safe:-translate-x-1 group-hover:translate-x-0 group-hover:opacity-100 group-has-[a:focus-visible]:opacity-100 dark:text-navy-300">
            Preview <ArrowRight className="h-3.5 w-3.5" />
          </span>
        </footer>
      </div>
    </article>
  )
}

function EmptyState({ search, onClear }) {
  return (
    <div className="px-6 py-16 text-center animate-panel-in">
      <div className="relative mx-auto h-20 w-20">
        <div className="absolute inset-0 rotate-6 rounded-2xl bg-navy-100/60 dark:bg-navy-500/10" />
        <div className="absolute inset-0 -rotate-3 rounded-2xl bg-white shadow-sm ring-1 ring-gray-200 dark:bg-gray-800 dark:ring-gray-700" />
        <div className="absolute inset-0 flex items-center justify-center">
          {search
            ? <FileSearch className="h-8 w-8 text-navy-500 dark:text-navy-300" />
            : <Files className="h-8 w-8 text-navy-500 dark:text-navy-300" />}
        </div>
      </div>
      <h3 className="mt-5 text-base font-semibold text-gray-900 dark:text-gray-100">
        {search ? 'No matching documents' : 'No documents yet'}
      </h3>
      <p className="mx-auto mt-1 max-w-sm text-sm text-gray-500 dark:text-gray-400">
        {search
          ? <>Nothing is named like &ldquo;{search}&rdquo;. Try part of the file name, or its extension such as &ldquo;.pdf&rdquo;.</>
          : 'Files show up here as they arrive through the email queue, the Outlook add-in, or new requests.'}
      </p>
      {search ? (
        <button
          type="button"
          onClick={onClear}
          className="mt-5 inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3.5 py-2 text-sm font-medium text-gray-700 transition-all duration-200 hover:-translate-y-0.5 hover:bg-gray-50 hover:shadow-sm active:translate-y-0 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
        >
          <X className="h-3.5 w-3.5" />
          Clear search
        </button>
      ) : (
        <Link
          to="/emails"
          className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-navy-600 transition-colors hover:text-navy-700 dark:text-navy-300 dark:hover:text-navy-200"
        >
          Open the email queue <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      )}
    </div>
  )
}

function Pagination({ page, totalPages, total, perPage, onPage, onPerPage }) {
  return (
    <div className="flex flex-col gap-3 border-t border-gray-100 px-4 py-3.5 text-sm text-gray-500 dark:border-gray-800 dark:text-gray-400 sm:flex-row sm:items-center sm:justify-between sm:px-5">
      <label className="flex items-center gap-2">
        Per page
        <select
          value={perPage}
          onChange={(e) => onPerPage(Number(e.target.value))}
          className="h-8 rounded-lg border border-gray-200 bg-white px-2 text-sm text-gray-700 focus:border-navy-500 focus:outline-none focus:ring-2 focus:ring-navy-500/25 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
        >
          {PAGE_SIZES.map(size => <option key={size} value={size}>{size}</option>)}
        </select>
      </label>
      {total > perPage && (
        <nav aria-label="Pagination" className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onPage(page - 1)}
            disabled={page <= 1}
            className="inline-flex h-8 items-center gap-1 rounded-lg px-2.5 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent dark:hover:bg-gray-800 dark:hover:text-gray-200"
          >
            <ChevronLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Previous</span>
          </button>
          {generatePageNumbers(page, totalPages).map((pg, i) => (
            <React.Fragment key={`${pg}-${i}`}>
              {pg === '...' ? (
                <span className="px-1.5 text-gray-400 dark:text-gray-500">…</span>
              ) : (
                <button
                  type="button"
                  onClick={() => onPage(pg)}
                  aria-current={page === pg ? 'page' : undefined}
                  className={`h-8 min-w-[2rem] rounded-lg px-2 text-sm font-medium tabular-nums transition-all duration-200 ${
                    page === pg
                      ? 'bg-navy-800 text-white shadow-sm'
                      : 'text-gray-600 hover:bg-gray-100 active:scale-95 dark:text-gray-400 dark:hover:bg-gray-800'
                  }`}
                >
                  {pg}
                </button>
              )}
            </React.Fragment>
          ))}
          <button
            type="button"
            onClick={() => onPage(page + 1)}
            disabled={page >= totalPages}
            className="inline-flex h-8 items-center gap-1 rounded-lg px-2.5 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent dark:hover:bg-gray-800 dark:hover:text-gray-200"
          >
            <span className="hidden sm:inline">Next</span>
            <ChevronRight className="h-4 w-4" />
          </button>
        </nav>
      )}
    </div>
  )
}

// ── Skeletons ────────────────────────────────────────────────────────────────

function ToolbarSkeleton() {
  return (
    <div className="flex items-center gap-3 border-b border-gray-100 px-5 py-3.5 dark:border-gray-800">
      <div className="skeleton h-9 w-full max-w-md rounded-lg" />
      <div className="ml-auto hidden gap-2 sm:flex">
        <div className="skeleton h-9 w-32 rounded-lg" />
        <div className="skeleton h-9 w-36 rounded-lg" />
      </div>
    </div>
  )
}

function DocumentRowSkeleton() {
  return (
    <div className={`grid items-center gap-4 px-5 py-3 ${LIST_COLUMNS}`}>
      <div className="flex items-center gap-3.5">
        <div className="skeleton h-10 w-10 rounded-xl" />
        <div className="flex-1 space-y-2">
          <div className="skeleton h-4 w-56 max-w-full" />
          <div className="skeleton h-3 w-24" />
        </div>
      </div>
      <div className="skeleton hidden h-5 w-20 rounded-full md:block" />
      <div className="skeleton hidden h-4 w-14 md:block" />
      <div className="skeleton hidden h-5 w-16 md:block" />
      <div className="skeleton hidden h-4 w-20 md:block" />
      <div />
    </div>
  )
}

function DocumentCardSkeleton() {
  return (
    <div className="flex flex-col rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
      <div className="flex h-32 items-center justify-center border-b border-gray-100 dark:border-gray-800">
        <div className="skeleton h-20 w-16 rounded-lg" />
      </div>
      <div className="space-y-2 p-4">
        <div className="skeleton h-4 w-4/5" />
        <div className="skeleton h-3 w-1/2" />
        <div className="skeleton mt-3 h-5 w-16" />
        <div className="border-t border-gray-100 pt-3 dark:border-gray-800">
          <div className="skeleton h-3 w-20" />
        </div>
      </div>
    </div>
  )
}

function generatePageNumbers(current, total) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1)
  }
  if (current <= 3) {
    return [1, 2, 3, 4, '...', total]
  }
  if (current >= total - 2) {
    return [1, '...', total - 3, total - 2, total - 1, total]
  }
  return [1, '...', current - 1, current, current + 1, '...', total]
}

export default Documents
